/**
 * Equipment Tuning Studio data model.
 *
 * The studio (`#tune-equipment` route) writes tuned offset / rotation /
 * scale values for individual GLTF items, and proportional fractions for
 * procedural body attachments (shoulder / belt / sash). Values land in
 * localStorage so a refresh keeps them; the user exports the JSON and
 * pastes it back to the dev to bake into items.ts / MeshAttachment.tsx.
 *
 * The runtime renderer (PlayerCharacter → MeshAttachment) reads from
 * localStorage IF the page also has `?tune` in the URL OR the studio is
 * the active screen — production builds outside the studio ignore the
 * tuning data entirely.
 */

export interface ItemMeshTune {
  offset: [number, number, number];
  rotation: [number, number, number];
  scale: number;
}

export interface ProceduralShoulderTune {
  yFrac: number;          // shoulder Y position as fraction of body height
  xOffsetFrac: number;    // X spread as fraction of torso half-width
  capRadiusFrac: number;  // pauldron radius as fraction of torso half-width
  tiltZ: number;          // outward tilt around Z (radians)
}

export interface ProceduralBeltTune {
  yFrac: number;          // belt Y position as fraction of body height
  radiusFrac: number;     // torus radius as fraction of torso half-width
  tubeFrac: number;       // tube thickness as fraction of torus radius
  buckleSize: number;     // buckle width as fraction of torus radius
}

export interface ProceduralSashTune {
  yFrac: number;          // sash centre Y as fraction of body height
  xOffsetFrac: number;    // sash X offset as fraction of torso half-width
  lengthFrac: number;     // strip length as fraction of body height
  widthFrac: number;      // strip width as fraction of torso half-width
  tiltZ: number;          // tilt around Z (radians)
  forwardFrac: number;    // Z forward offset as fraction of torso half-depth
}

export interface ProceduralBodyTune {
  shoulder: ProceduralShoulderTune;
  belt:     ProceduralBeltTune;
  sash:     ProceduralSashTune;
}

export interface TuningOverrides {
  /** Per-item GLTF transform overrides (key = ItemDef.id). */
  items: Record<string, ItemMeshTune | undefined>;
  /** Per-body procedural-shape overrides (key = body GLTF path). */
  procedural: Record<string, Partial<ProceduralBodyTune> | undefined>;
}

// ── Defaults — match the hardcoded values currently in MeshAttachment ──

export const DEFAULT_PROC_SHOULDER: ProceduralShoulderTune = {
  yFrac: 0.78,
  xOffsetFrac: 0.95,
  capRadiusFrac: 0.55,
  tiltZ: 0.18,
};

export const DEFAULT_PROC_BELT: ProceduralBeltTune = {
  yFrac: 0.55,
  radiusFrac: 1.05,
  tubeFrac: 0.10,
  buckleSize: 0.45,
};

export const DEFAULT_PROC_SASH: ProceduralSashTune = {
  yFrac: 0.67,
  xOffsetFrac: 0.30,
  lengthFrac: 0.30,
  widthFrac: 0.30,
  tiltZ: 0.45,
  forwardFrac: 1.00,
};

export const DEFAULT_PROC_BODY: ProceduralBodyTune = {
  shoulder: DEFAULT_PROC_SHOULDER,
  belt:     DEFAULT_PROC_BELT,
  sash:     DEFAULT_PROC_SASH,
};

export const EMPTY_TUNING: TuningOverrides = {
  items: {},
  procedural: {},
};

// ── localStorage IO ────────────────────────────────────────────────────

const STORAGE_KEY = 'arctic-game-tuning-v1';

export function loadTuning(): TuningOverrides {
  if (typeof localStorage === 'undefined') return EMPTY_TUNING;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_TUNING;
    const parsed = JSON.parse(raw) as TuningOverrides;
    if (!parsed || typeof parsed !== 'object') return EMPTY_TUNING;
    return {
      items: parsed.items ?? {},
      procedural: parsed.procedural ?? {},
    };
  } catch {
    return EMPTY_TUNING;
  }
}

export function saveTuning(tuning: TuningOverrides): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tuning));
  } catch {
    // QuotaExceeded or similar — silent
  }
}

export function clearTuning(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}

/** Pretty-printed JSON for the export button. */
export function exportTuning(tuning: TuningOverrides): string {
  return JSON.stringify(tuning, null, 2);
}

// ── Lookup helpers used by renderer ────────────────────────────────────

export function getItemMeshTune(
  tuning: TuningOverrides | null,
  itemId: string,
): ItemMeshTune | undefined {
  return tuning?.items?.[itemId];
}

export function getProceduralTune(
  tuning: TuningOverrides | null,
  bodyPath: string,
  shape: 'shoulder' | 'belt' | 'sash',
): ProceduralShoulderTune | ProceduralBeltTune | ProceduralSashTune {
  const body = tuning?.procedural?.[bodyPath];
  switch (shape) {
    case 'shoulder': return body?.shoulder ?? DEFAULT_PROC_SHOULDER;
    case 'belt':     return body?.belt     ?? DEFAULT_PROC_BELT;
    case 'sash':     return body?.sash     ?? DEFAULT_PROC_SASH;
  }
}
